import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Chip, Typography, Card, CardContent,
  CircularProgress, Alert, Collapse, Divider, Tooltip, IconButton,
  List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axios from 'axios';

const TOOL_LABELS = {
  group_members: 'Участники сообществ',
  audience_filter: 'Фильтр аудитории',
  active_audience: 'Активная аудитория',
};

const EXAMPLES = [
  'девушки из Москвы до 20 лет, без отношений',
  'мужчины 30–40 из Санкт-Петербурга, активные',
  'подписчики Моргенштерна из Новосибирска',
  'мамы 25–35 с аватаркой',
  'парни 18–25 студенты МГУ',
];

export default function SmartQueryBar({ onApply, disabled }) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [presets, setPresets] = useState(EXAMPLES);
  const [showExamples, setShowExamples] = useState(false);

  useEffect(() => {
    axios.get('/smart-query/presets')
      .then(r => setPresets(r.data.presets || EXAMPLES))
      .catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setResult(null);
    setError('');
    try {
      const res = await axios.post('/smart-query/parse', { query });
      if (res.data.error && !res.data.filters && Object.keys(res.data.filters || {}).length === 0) {
        setError(res.data.error);
      } else {
        setResult(res.data);
      }
    } catch (e) {
      setError('Не удалось разобрать запрос. Попробуйте переформулировать.');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!result) return;
    onApply(result.filters || {}, result.source_hint || null);
    setResult(null);
    setQuery('');
  };

  const handleDismiss = () => {
    setResult(null);
    setError('');
  };

  const hasFilters = result && Object.keys(result.filters || {}).length > 0;
  const hasSource = result?.source_hint;
  const hasUnresolved = result?.unresolved?.length > 0;
  const isEmpty = result && !hasFilters && !hasSource;

  return (
    <Box sx={{ mb: 3 }}>
      {/* Input row */}
      <Card sx={{ border: '1px solid rgba(108,99,255,0.25)', bgcolor: 'rgba(108,99,255,0.04)' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {/* Header row: icon + label + help */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <AutoFixHighIcon sx={{ color: '#6C63FF', fontSize: 18, flexShrink: 0 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1, color: 'text.primary' }}>
              Умный запрос
            </Typography>
            <Tooltip
              title={
                <Box>
                  <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>Примеры запросов:</Typography>
                  {EXAMPLES.map((e, i) => <Typography key={i} variant="caption" display="block">• {e}</Typography>)}
                </Box>
              }
              placement="bottom-end"
              arrow
            >
              <IconButton size="small" onClick={() => setShowExamples(v => !v)} aria-label="Показать примеры">
                <HelpOutlineIcon fontSize="small" sx={{ color: 'action.active' }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Input + button row */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder='Опишите аудиторию: "девушки 20–25 из Москвы, без пары, с аватаркой"'
              value={query}
              onChange={e => { setQuery(e.target.value); setResult(null); setError(''); }}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
              disabled={disabled || loading}
              inputProps={{ maxLength: 500 }}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.03)' } }}
            />
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={disabled || loading || !query.trim()}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ flexShrink: 0, alignSelf: 'flex-start', whiteSpace: 'nowrap' }}
            >
              {loading ? 'Разбираем...' : 'Собрать'}
            </Button>
          </Box>

          {/* Quick presets */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {presets.map((p, i) => (
              <Chip
                key={i}
                label={p}
                size="small"
                variant="outlined"
                onClick={() => { setQuery(p); setResult(null); setError(''); }}
                sx={{
                  fontSize: 11,
                  borderColor: 'rgba(108,99,255,0.3)',
                  color: 'text.secondary',
                  cursor: 'pointer',
                  '&:hover': { borderColor: '#6C63FF', color: '#fff', bgcolor: 'rgba(108,99,255,0.12)' },
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Error */}
      <Collapse in={!!error}>
        <Alert
          severity="warning"
          sx={{ mt: 1 }}
          onClose={() => setError('')}
          action={<Button size="small" color="inherit" onClick={() => setError('')}>OK</Button>}
        >
          {error}
        </Alert>
      </Collapse>

      {/* Plan card */}
      <Collapse in={!!result}>
        {result && (
          <Card sx={{
            mt: 1,
            border: isEmpty
              ? '1px solid rgba(255,167,38,0.4)'
              : '1px solid rgba(108,99,255,0.35)',
            bgcolor: 'rgba(108,99,255,0.04)',
          }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: isEmpty ? '#FFB74D' : '#6C63FF' }}>
                  {isEmpty ? 'Не удалось разобрать запрос' : 'План сбора аудитории'}
                </Typography>
                <IconButton size="small" onClick={handleDismiss} aria-label="Закрыть">
                  <CloseIcon fontSize="small" sx={{ color: 'action.active' }} />
                </IconButton>
              </Box>

              {isEmpty && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Не удалось распознать параметры. Попробуйте переформулировать или заполните форму вручную.
                </Alert>
              )}

              {/* Tool chain */}
              {result.tool_chain?.length > 0 && !isEmpty && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
                  {result.tool_chain.map((tool, i) => (
                    <React.Fragment key={tool}>
                      <Chip
                        label={TOOL_LABELS[tool] || tool}
                        size="small"
                        sx={{ bgcolor: 'rgba(108,99,255,0.18)', color: '#A89DFF', fontWeight: 600, fontSize: 11 }}
                      />
                      {i < result.tool_chain.length - 1 && (
                        <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              )}

              {/* Detected filters */}
              {hasFilters && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.75, textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.8 }}>
                    Распознанные параметры
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {(result.labels || []).filter(l => !l.startsWith('Источник')).map((label, i) => (
                      <Chip
                        key={i}
                        icon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
                        label={label}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(0,212,170,0.12)',
                          color: '#00D4AA',
                          border: '1px solid rgba(0,212,170,0.25)',
                          '& .MuiChip-icon': { color: '#00D4AA' },
                          fontSize: 11,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Source hint */}
              {hasSource && (
                <Box sx={{ mb: 1.5 }}>
                  <Divider sx={{ mb: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.75, textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.8 }}>
                    Источник
                  </Typography>
                  <Alert
                    severity="info"
                    icon={false}
                    sx={{
                      bgcolor: 'rgba(108,99,255,0.1)',
                      border: '1px solid rgba(108,99,255,0.2)',
                      color: 'text.primary',
                      py: 0.5,
                    }}
                  >
                    <Typography variant="body2">
                      Поиск групп по запросу:{' '}
                      <Box component="span" sx={{ fontWeight: 700, color: '#A89DFF' }}>
                        «{result.source_hint.query}»
                      </Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Введите это название в поле ID сообщества или найдите группу вручную.
                    </Typography>
                  </Alert>
                </Box>
              )}

              {/* Unresolved */}
              {hasUnresolved && (
                <Box sx={{ mb: 1.5 }}>
                  <Divider sx={{ mb: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.75, textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.8 }}>
                    Не удалось распознать автоматически
                  </Typography>
                  <List dense disablePadding>
                    {result.unresolved.map((msg, i) => (
                      <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <WarningAmberIcon sx={{ fontSize: 14, color: '#FFB74D' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={msg}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Actions */}
              {!isEmpty && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleApply}
                    disabled={!hasFilters && !hasSource}
                  >
                    Применить фильтры
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDismiss}
                  >
                    Изменить запрос
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Collapse>
    </Box>
  );
}
